import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import AdminCard from "./AdminCard";

export default function UchwalyAdmin() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState({ title: "", number: "", year: new Date().getFullYear(), status: "obowiazujaca", pdf_url: "", doc_url: "", description: "" });
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterYear, setFilterYear] = useState("all");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 8;
    const resetForm = () => { setForm({ title: "", number: "", year: new Date().getFullYear(), status: "obowiazujaca", pdf_url: "", doc_url: "", description: "" }); setEditingId(null); };
    const load = () => api.get("/resolutions").then((r) => setItems(r.data || []));
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...form, year: Number(form.year) };
            if (editingId) {
                await api.put(`/resolutions/${editingId}`, payload);
                toast.success("Uchwała zaktualizowana.");
            } else {
                await api.post("/resolutions", payload);
                toast.success("Uchwała dodana.");
            }
            resetForm();
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const startEdit = (it) => {
        setForm({ title: it.title, number: it.number, year: it.year, status: it.status || "obowiazujaca", pdf_url: it.pdf_url || "", doc_url: it.doc_url || "", description: it.description || "" });
        setEditingId(it.id);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const del = async (id) => {
        if (!window.confirm("Usunąć uchwałę?")) return;
        await api.delete(`/resolutions/${id}`);
        if (editingId === id) resetForm();
        await load();
    };

    const years = Array.from(new Set(items.map((i) => i.year))).sort((a, b) => b - a);
    const filtered = filterYear === "all" ? items : items.filter((i) => String(i.year) === String(filterYear));
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return (
        <div className="grid lg:grid-cols-2 gap-6">
            <AdminCard>
                <h3 className="font-serif text-xl text-[#092E22] mb-5">{editingId ? "Edycja uchwały" : "Nowa uchwała"}</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="admin-res-form">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2"><Label>Numer *</Label><Input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="1/2026" data-testid="admin-res-number" /></div>
                        <div className="space-y-2"><Label>Rok *</Label><Input required type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="admin-res-year" /></div>
                    </div>
                    <div className="space-y-2"><Label>Tytuł *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-res-title" /></div>
                    <div className="space-y-2"><Label>Opis</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-res-description" /></div>
                    <div className="space-y-2">
                        <Label>Plik PDF uchwały</Label>
                        <FileUpload
                            accept=".pdf"
                            label="Wgraj PDF z komputera"
                            testid="admin-res-pdf-upload"
                            onUploaded={(data) => setForm((f) => ({ ...f, pdf_url: data.url }))}
                        />
                        {form.pdf_url && (
                            <p className="text-xs text-gray-500 break-all">
                                Aktualny plik: <span className="font-mono text-[#0D4F3C]">{form.pdf_url}</span>
                            </p>
                        )}
                        <Input
                            value={form.pdf_url}
                            onChange={(e) => setForm({ ...form, pdf_url: e.target.value })}
                            placeholder="...lub wklej link zewnętrzny (Google Drive itp.)"
                            data-testid="admin-res-pdf"
                            className="text-xs"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Plik DOC / DOCX uchwały (opcjonalnie)</Label>
                        <FileUpload
                            accept=".doc,.docx"
                            label="Wgraj DOC/DOCX z komputera"
                            testid="admin-res-doc-upload"
                            onUploaded={(data) => setForm((f) => ({ ...f, doc_url: data.url }))}
                        />
                        {form.doc_url && (
                            <p className="text-xs text-gray-500 break-all">
                                Aktualny plik: <span className="font-mono text-[#0D4F3C]">{form.doc_url}</span>
                                <button type="button" onClick={() => setForm((f) => ({ ...f, doc_url: "" }))} className="ml-2 text-red-500 hover:underline" data-testid="admin-res-doc-clear">Usuń</button>
                            </p>
                        )}
                        <Input
                            value={form.doc_url}
                            onChange={(e) => setForm({ ...form, doc_url: e.target.value })}
                            placeholder="...lub wklej link zewnętrzny"
                            data-testid="admin-res-doc"
                            className="text-xs"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="admin-res-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="obowiazujaca">Obowiązująca</SelectItem>
                                <SelectItem value="archiwalna">Archiwalna</SelectItem>
                                <SelectItem value="projekt">Projekt</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button type="submit" disabled={loading} data-testid="admin-res-submit" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : (editingId ? "Zapisz zmiany" : "Dodaj uchwałę")}
                    </Button>
                    {editingId && (
                        <Button type="button" variant="outline" onClick={resetForm} data-testid="admin-res-cancel" className="rounded-full ml-2">Anuluj</Button>
                    )}
                </form>
            </AdminCard>

            <AdminCard>
                <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
                    <h3 className="font-serif text-xl text-[#092E22]">Lista uchwał ({filtered.length})</h3>
                    <Select value={String(filterYear)} onValueChange={(v) => { setFilterYear(v); setPage(1); }}>
                        <SelectTrigger className="w-[150px]" data-testid="admin-res-year-filter"><SelectValue placeholder="Rok" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Wszystkie lata</SelectItem>
                            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-3">
                    {pageItems.length === 0 && <p className="text-gray-500">Brak uchwał.</p>}
                    {pageItems.map((it) => (
                        <div key={it.id} className={`p-4 rounded-2xl bg-[#FAF8F3] border ${editingId === it.id ? "border-[#D4A537]" : "border-gray-100"}`} data-testid={`admin-res-item-${it.id}`}>
                            <div className="flex justify-between items-start gap-3 mb-1">
                                <div className="flex gap-2 items-center flex-wrap">
                                    <span className="text-xs font-mono text-[#D4A537] font-semibold">{it.number}</span>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-gray-500">{it.year}</span>
                                    <Badge variant="secondary" className="text-[10px] uppercase">{it.status}</Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => startEdit(it)} className="text-[#0D4F3C] hover:text-[#092E22] p-1.5 rounded-lg hover:bg-white" data-testid={`admin-res-edit-${it.id}`} title="Edytuj"><Pencil size={14} /></button>
                                    <button onClick={() => del(it.id)} className="text-red-500 hover:text-red-700 p-1.5 rounded-lg hover:bg-white" data-testid={`admin-res-delete-${it.id}`} title="Usuń"><Trash2 size={14} /></button>
                                </div>
                            </div>
                            <p className="font-serif text-base text-[#092E22]">{it.title}</p>
                        </div>
                    ))}
                </div>
                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-5" data-testid="admin-res-pagination">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="px-3 py-1.5 rounded-full border border-gray-200 text-sm disabled:opacity-40 hover:bg-[#FAF8F3]">‹ Poprzednia</button>
                        <span className="text-sm text-gray-600">Strona {safePage} z {totalPages}</span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="px-3 py-1.5 rounded-full border border-gray-200 text-sm disabled:opacity-40 hover:bg-[#FAF8F3]">Następna ›</button>
                    </div>
                )}
            </AdminCard>
        </div>
    );
}
